import { createSlice } from '@reduxjs/toolkit';
import { request } from '../service/api/serviceRequest';
import { constructEndpoint } from '../utils/commonHelper';
import { LOADING_KEYS } from '../constants/loadingKeys';
import {
  SUCCESS_STATUS,
  SSO,
  GET_SSO_DOMAINS,
  CREATE_SSO_DOMAIN,
  GET_SSO_CLAIM_CONFIG,
  UPDATE_SSO_CLAIM_CONFIG,
  GET_SSO_SITE_CODES,
  CREATE_SSO_SITE_CODE,
  UPDATE_SSO_SITE_CODE,
  DELETE_SSO_SITE_CODE,
  GET_SSO_QUALIFIERS,
  CREATE_SSO_QUALIFIER,
  UPDATE_SSO_QUALIFIER,
  DELETE_SSO_QUALIFIER,
  GET_SSO_VALUE_MAPPINGS,
  CREATE_SSO_VALUE_MAPPING,
  UPDATE_SSO_VALUE_MAPPING,
  DELETE_SSO_VALUE_MAPPING,
  GET_SSO_LOGIN_RESOLUTIONS,
  GET_SSO_LOGIN_RESOLUTION,
  GET_SSO_OBSERVED_CLAIMS,
  SSO_RESOLVER_TEST,
  GET_USERS_PENDING_APPROVAL,
  APPROVE_PENDING_USER,
} from '../constants';

/**
 * State for the new SSO administration surface (claim pipeline, site codes, qualifiers,
 * access rules, migration, tester, resolutions, pending approvals).
 *
 * Kept as its own slice rather than folding into userSlice (2300+ lines already) per the
 * handover: this surface has an independent lifecycle from the legacy SSO CRUD screens.
 *
 * The backend for every one of these endpoints is being built in parallel and may not exist
 * yet, so every thunk here swallows network/HTTP errors and resets its slice to an empty
 * value instead of rethrowing, and skips the global error toast (skipToast) for the same
 * reason: a 404 during rollout is expected, not actionable by the admin looking at the screen.
 */
const initialState = {
  /**
   * The SSO domains themselves. This is the entry point to every other screen here:
   * they are all keyed on an ssoDomainId, and the legacy list cannot supply one - it
   * renders SSODomainTenantMapping grants and navigates with a mapping id, so a domain
   * configured purely by grammar had no row to click at all.
   */
  domains: { data: [], metadata: null, status: 'idle' },
  /** The legacy grant rules belonging to one domain, for the Legacy rules tab. */
  domainLegacyRules: { data: [], total: 0, status: 'idle' },
  claimConfig: { data: null, status: 'idle' },
  siteCodes: { data: [], status: 'idle' },
  qualifiers: { data: [], status: 'idle' },
  accessRules: { data: [], status: 'idle' },
  resolutions: { items: [], total: 0, distinctClaims: null, status: 'idle' },
  /**
   * The Overview strip's environment-wide login counts.
   *
   * Deliberately NOT the `resolutions` branch above. Both are fed by the same endpoint but
   * ask different questions - the strip asks about every domain over seven days, the Login
   * resolutions tab asks about one domain under the admin's filters - so sharing one branch
   * meant whichever answered last decided what both displayed.
   */
  overview: { total: null, fallbackCount: null, distinctClaims: null, status: 'idle' },
  resolutionDetail: { data: null, status: 'idle' },
  observedClaims: { data: [], status: 'idle' },
  resolverTest: { request: null, result: null, status: 'idle' },
  pending: { items: [], status: 'idle' },
};

const ssoConfigSlice = createSlice({
  name: 'ssoConfig',
  initialState,
  reducers: {
    setSsoDomains(state, action) {
      state.domains = {
        data: action.payload?.ssoDomainList || [],
        metadata: action.payload?.metadata || null,
        status: 'succeeded',
      };
    },
    setSsoDomainsFailed(state) {
      state.domains = { data: [], metadata: null, status: 'failed' };
    },
    setDomainLegacyRules(state, action) {
      state.domainLegacyRules = {
        data: action.payload?.rules || [],
        // The endpoint pages. Without the server's total the screen cannot tell a domain with
        // 200 rules from one with 2000, and would quietly show only the first page.
        total: action.payload?.total ?? (action.payload?.rules || []).length,
        status: 'succeeded',
      };
    },
    setDomainLegacyRulesFailed(state) {
      state.domainLegacyRules = { data: [], total: 0, status: 'failed' };
    },
    setClaimConfig(state, action) {
      state.claimConfig = { data: action.payload || null, status: 'succeeded' };
    },
    /**
     * Deliberately distinct from `setClaimConfig(null)`. A successful GET returning null means
     * the domain has no configuration yet; a failed GET means we do not know what it has. The
     * two must not look alike, because the screens offer to save their defaults over whatever
     * is actually stored.
     */
    setClaimConfigFailed(state) {
      state.claimConfig = { data: null, status: 'failed' };
    },
    setSiteCodes(state, action) {
      state.siteCodes = { data: action.payload || [], status: 'succeeded' };
    },
    setSiteCodesFailed(state) {
      state.siteCodes = { data: [], status: 'failed' };
    },
    setQualifiers(state, action) {
      state.qualifiers = { data: action.payload || [], status: 'succeeded' };
    },
    setQualifiersFailed(state) {
      state.qualifiers = { data: [], status: 'failed' };
    },
    setAccessRules(state, action) {
      state.accessRules = { data: action.payload || [], status: 'succeeded' };
    },
    setAccessRulesFailed(state) {
      state.accessRules = { data: [], status: 'failed' };
    },
    setResolutions(state, action) {
      state.resolutions = {
        items: action.payload?.items || [],
        total: action.payload?.total || 0,
        distinctClaims: action.payload?.distinctClaims,
        status: 'succeeded',
      };
    },
    setResolutionsFailed(state) {
      state.resolutions = { items: [], total: 0, distinctClaims: null, status: 'failed' };
    },
    setOverviewStats(state, action) {
      state.overview = {
        total: action.payload?.total ?? 0,
        fallbackCount: action.payload?.fallbackCount ?? 0,
        distinctClaims: action.payload?.distinctClaims ?? null,
        status: 'succeeded',
      };
    },
    setOverviewStatsFailed(state) {
      state.overview = { total: null, fallbackCount: null, distinctClaims: null, status: 'failed' };
    },
    setResolutionDetail(state, action) {
      state.resolutionDetail = { data: action.payload || null, status: 'succeeded' };
    },
    setObservedClaims(state, action) {
      state.observedClaims = { data: action.payload || [], status: 'succeeded' };
    },
    setObservedClaimsFailed(state) {
      state.observedClaims = { data: [], status: 'failed' };
    },
    setResolverTestRequest(state, action) {
      state.resolverTest = { request: action.payload, result: state.resolverTest.result, status: 'loading' };
    },
    setResolverTestResult(state, action) {
      state.resolverTest = { ...state.resolverTest, result: action.payload, status: 'succeeded' };
    },
    setPending(state, action) {
      state.pending = { items: action.payload?.items || [], status: 'succeeded' };
    },
    setPendingFailed(state) {
      state.pending = { items: [], status: 'failed' };
    },
    resetSsoConfig() {
      return initialState;
    },
  },
});

export const {
  setSsoDomains,
  setSsoDomainsFailed,
  setDomainLegacyRules,
  setDomainLegacyRulesFailed,
  setClaimConfig,
  setClaimConfigFailed,
  setSiteCodes,
  setSiteCodesFailed,
  setQualifiers,
  setQualifiersFailed,
  setAccessRules,
  setAccessRulesFailed,
  setResolutions,
  setResolutionsFailed,
  setOverviewStats,
  setOverviewStatsFailed,
  setResolutionDetail,
  setObservedClaims,
  setObservedClaimsFailed,
  setResolverTestRequest,
  setResolverTestResult,
  setPending,
  setPendingFailed,
  resetSsoConfig,
} = ssoConfigSlice.actions;

export default ssoConfigSlice.reducer;

/* --------------------------------- Domains (list) --------------------------------- */

/**
 * Lists the SSO domains, with a count of what each has configured.
 *
 * Unlike the thunks below this one does NOT swallow its error silently into an empty
 * list without saying so: an empty domain list and a failed call look identical on
 * screen, and the screen's whole job is to tell an admin which domains exist. The
 * status is set so the list can distinguish the two.
 */
export const getSsoDomainsAsync = (params = {}) => async (dispatch) => {
  const { limit = 25, offSet = 0, search = '', sort = '' } = params;
  const query = new URLSearchParams({
    Limit: String(limit),
    Offset: String(offSet),
    Search: search,
    Sort: sort,
  }).toString();

  try {
    const data = await request(`${GET_SSO_DOMAINS}?${query}`, {
      method: 'GET',
      loadingKey: LOADING_KEYS.SSO_DOMAINS,
      skipToast: true,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(setSsoDomains(data.data));
    } else {
      dispatch(setSsoDomainsFailed());
    }
    return data;
  } catch (error) {
    dispatch(setSsoDomainsFailed());
    return error;
  }
};

/**
 * Creates a domain and refreshes the list so the caller can navigate straight into it.
 *
 * The toast is not suppressed here, unlike the list read above: this one writes, and the
 * server's message distinguishes a fresh domain from a previously removed one that has been
 * restored - a difference the admin needs to know about, because a restored domain may still
 * carry site codes and rules from before.
 */
export const createSsoDomainAsync = (domainName) => async (dispatch) => {
  try {
    const data = await request(CREATE_SSO_DOMAIN, {
      method: 'POST',
      body: JSON.stringify({ domainName }),
      loadingKey: LOADING_KEYS.SSO_DOMAINS,
    });
    if (data?.status === SUCCESS_STATUS) {
      await dispatch(getSsoDomainsAsync({ limit: 100, offSet: 0 }));
    }
    return data;
  } catch (error) {
    return error;
  }
};

/**
 * The legacy grant rules for one domain.
 *
 * Filtered by ssoDomainId server side, deliberately not by passing the domain name to the
 * list endpoint's free-text Search: that also matches Tenant and the e-mail lists, so a
 * name search could show another domain's rules on a tab that claims they are this one's.
 */
export const getDomainLegacyRulesAsync = (ssoDomainId) => async (dispatch) => {
  const query = new URLSearchParams({
    Limit: '200',
    Offset: '0',
    Search: '',
    Sort: '',
    ssoDomainId: String(ssoDomainId),
  }).toString();

  try {
    const data = await request(`${SSO}?${query}`, {
      method: 'GET',
      loadingKey: LOADING_KEYS.SSO_DOMAIN_LEGACY_RULES,
      skipToast: true,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(
        setDomainLegacyRules({
          rules: data.data?.ssoTenantMappingList || [],
          total: data.data?.metadata?.total,
        })
      );
    } else {
      dispatch(setDomainLegacyRulesFailed());
    }
    return data;
  } catch (error) {
    dispatch(setDomainLegacyRulesFailed());
    return error;
  }
};

/* ---------------------------- Claim pipeline (domain detail) ---------------------------- */

export const getSsoClaimConfigAsync = (ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(constructEndpoint(GET_SSO_CLAIM_CONFIG, ssoDomainId), {
      method: 'GET',
      loadingKey: LOADING_KEYS.SSO_CLAIM_CONFIG,
      skipToast: true,
    });
    // A success carrying null is "this domain has no claim config yet" and is normal. Anything
    // else is a failure, and must not be shown as an unconfigured domain: the screens would
    // then offer to save their defaults over a configuration that is actually there.
    if (data?.status === SUCCESS_STATUS) dispatch(setClaimConfig(data.data));
    else dispatch(setClaimConfigFailed());
  } catch {
    dispatch(setClaimConfigFailed());
  }
};

export const updateSsoClaimConfigAsync = (payload) => async (dispatch) => {
  try {
    const data = await request(UPDATE_SSO_CLAIM_CONFIG, {
      method: 'PUT',
      body: JSON.stringify(payload),
      loadingKey: LOADING_KEYS.SSO_CLAIM_CONFIG,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(setClaimConfig(data.data || payload));
    }
    return data;
  } catch (error) {
    return error;
  }
};

/* ---------------------------------- Site codes ---------------------------------- */

export const getSsoSiteCodesAsync = (ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(constructEndpoint(GET_SSO_SITE_CODES, ssoDomainId), {
      method: 'GET',
      loadingKey: LOADING_KEYS.SSO_SITE_CODES,
      skipToast: true,
    });
    if (data?.status === SUCCESS_STATUS) dispatch(setSiteCodes(data.data?.siteCodes));
    else dispatch(setSiteCodesFailed());
  } catch {
    dispatch(setSiteCodesFailed());
  }
};

export const createSsoSiteCodeAsync = (payload, ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(CREATE_SSO_SITE_CODE, {
      method: 'POST',
      body: JSON.stringify(payload),
      loadingKey: LOADING_KEYS.SSO_SITE_CODES,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(getSsoSiteCodesAsync(ssoDomainId));
    }
    return data;
  } catch (error) {
    return error;
  }
};

export const updateSsoSiteCodeAsync = (payload, ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(UPDATE_SSO_SITE_CODE, {
      method: 'PUT',
      body: JSON.stringify(payload),
      loadingKey: LOADING_KEYS.SSO_SITE_CODES,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(getSsoSiteCodesAsync(ssoDomainId));
    }
    return data;
  } catch (error) {
    return error;
  }
};

// `id` here is the one the list endpoint returns for each row (getssositecodes projects
// `id = x.Id`), and the route the API declares is deletessositecode/{id}. Same for the
// qualifier and value-mapping deletes below.
export const deleteSsoSiteCodeAsync = (id, ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(constructEndpoint(DELETE_SSO_SITE_CODE, id), {
      method: 'DELETE',
      loadingKey: LOADING_KEYS.SSO_SITE_CODES,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(getSsoSiteCodesAsync(ssoDomainId));
    }
    return data;
  } catch (error) {
    return error;
  }
};

/* ---------------------------------- Qualifiers ---------------------------------- */

export const getSsoQualifiersAsync = (ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(constructEndpoint(GET_SSO_QUALIFIERS, ssoDomainId), {
      method: 'GET',
      loadingKey: LOADING_KEYS.SSO_QUALIFIERS,
      skipToast: true,
    });
    if (data?.status === SUCCESS_STATUS) dispatch(setQualifiers(data.data?.qualifiers));
    else dispatch(setQualifiersFailed());
  } catch {
    dispatch(setQualifiersFailed());
  }
};

export const createSsoQualifierAsync = (payload, ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(CREATE_SSO_QUALIFIER, {
      method: 'POST',
      body: JSON.stringify(payload),
      loadingKey: LOADING_KEYS.SSO_QUALIFIERS,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(getSsoQualifiersAsync(ssoDomainId));
    }
    return data;
  } catch (error) {
    return error;
  }
};

export const updateSsoQualifierAsync = (payload, ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(UPDATE_SSO_QUALIFIER, {
      method: 'PUT',
      body: JSON.stringify(payload),
      loadingKey: LOADING_KEYS.SSO_QUALIFIERS,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(getSsoQualifiersAsync(ssoDomainId));
    }
    return data;
  } catch (error) {
    return error;
  }
};

export const deleteSsoQualifierAsync = (id, ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(constructEndpoint(DELETE_SSO_QUALIFIER, id), {
      method: 'DELETE',
      loadingKey: LOADING_KEYS.SSO_QUALIFIERS,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(getSsoQualifiersAsync(ssoDomainId));
    }
    return data;
  } catch (error) {
    return error;
  }
};

/* ------------------------------- Access rules (value mappings) ------------------------------- */

export const getSsoAccessRulesAsync = (ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(constructEndpoint(GET_SSO_VALUE_MAPPINGS, ssoDomainId), {
      method: 'GET',
      loadingKey: LOADING_KEYS.SSO_ACCESS_RULES,
      skipToast: true,
    });
    if (data?.status === SUCCESS_STATUS) dispatch(setAccessRules(data.data?.rules));
    else dispatch(setAccessRulesFailed());
  } catch {
    dispatch(setAccessRulesFailed());
  }
};

export const createSsoAccessRuleAsync = (payload, ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(CREATE_SSO_VALUE_MAPPING, {
      method: 'POST',
      body: JSON.stringify(payload),
      loadingKey: LOADING_KEYS.SSO_ACCESS_RULES,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(getSsoAccessRulesAsync(ssoDomainId));
    }
    return data;
  } catch (error) {
    return error;
  }
};

export const updateSsoAccessRuleAsync = (payload, ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(UPDATE_SSO_VALUE_MAPPING, {
      method: 'PUT',
      body: JSON.stringify(payload),
      loadingKey: LOADING_KEYS.SSO_ACCESS_RULES,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(getSsoAccessRulesAsync(ssoDomainId));
    }
    return data;
  } catch (error) {
    return error;
  }
};

export const deleteSsoAccessRuleAsync = (id, ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(constructEndpoint(DELETE_SSO_VALUE_MAPPING, id), {
      method: 'DELETE',
      loadingKey: LOADING_KEYS.SSO_ACCESS_RULES,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(getSsoAccessRulesAsync(ssoDomainId));
    }
    return data;
  } catch (error) {
    return error;
  }
};

/* ------------------------------------- Login resolutions ------------------------------------- */

export const getSsoLoginResolutionsAsync = (filters) => async (dispatch) => {
  try {
    const data = await request(constructEndpoint(GET_SSO_LOGIN_RESOLUTIONS, { ...filters }), {
      method: 'GET',
      loadingKey: LOADING_KEYS.SSO_LOGIN_RESOLUTIONS,
      skipToast: true,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(
        setResolutions({
          items: data.data?.items,
          total: data.data?.total,
          distinctClaims: data.data?.distinctClaims,
        })
      );
    } else {
      dispatch(setResolutionsFailed());
    }
  } catch {
    dispatch(setResolutionsFailed());
  }
};

/**
 * The Overview strip's own read of the same endpoint.
 *
 * Separate from getSsoLoginResolutionsAsync because the two ask different questions of it:
 * this one is environment-wide and windowed to the period the strip's labels promise, the
 * other is one domain under the admin's filters. They used to write the same slice branch,
 * so opening the Login resolutions tab silently rewrote the Overview counters with a single
 * domain's numbers, and the strip's own call overwrote the tab's filtered page.
 */
export const getSsoOverviewStatsAsync = (params = {}) => async (dispatch) => {
  try {
    const data = await request(constructEndpoint(GET_SSO_LOGIN_RESOLUTIONS, { ...params }), {
      method: 'GET',
      loadingKey: LOADING_KEYS.SSO_LOGIN_RESOLUTIONS,
      skipToast: true,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(
        setOverviewStats({
          total: data.data?.total,
          // Counted from the returned page, so it is only exact while the window fits in one
          // page - which is why the caller asks for a page large enough to hold it.
          fallbackCount: (data.data?.items || []).filter((item) => item.outcome === 'GrantedByFallback').length,
          distinctClaims: data.data?.distinctClaims,
        })
      );
    } else {
      dispatch(setOverviewStatsFailed());
    }
  } catch {
    dispatch(setOverviewStatsFailed());
  }
};

export const getSsoLoginResolutionAsync = (id) => async (dispatch) => {
  try {
    const data = await request(constructEndpoint(GET_SSO_LOGIN_RESOLUTION, id), {
      method: 'GET',
      loadingKey: LOADING_KEYS.SSO_LOGIN_RESOLUTIONS,
      skipToast: true,
    });
    dispatch(setResolutionDetail(data?.status === SUCCESS_STATUS ? data.data : null));
  } catch {
    dispatch(setResolutionDetail(null));
  }
};

export const getSsoObservedClaimsAsync = (ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(constructEndpoint(GET_SSO_OBSERVED_CLAIMS, ssoDomainId), {
      method: 'GET',
      loadingKey: LOADING_KEYS.SSO_LOGIN_RESOLUTIONS,
      skipToast: true,
    });
    if (data?.status === SUCCESS_STATUS) dispatch(setObservedClaims(data.data?.claims));
    else dispatch(setObservedClaimsFailed());
  } catch {
    dispatch(setObservedClaimsFailed());
  }
};

/* ----------------------------------------- Tester ----------------------------------------- */

export const runSsoResolverTestAsync = (payload) => async (dispatch) => {
  dispatch(setResolverTestRequest(payload));
  try {
    const data = await request(SSO_RESOLVER_TEST, {
      method: 'POST',
      body: JSON.stringify(payload),
      loadingKey: LOADING_KEYS.SSO_RESOLVER_TEST,
      skipToast: true,
    });
    dispatch(setResolverTestResult(data?.status === SUCCESS_STATUS ? data.data : null));
    return data;
  } catch (error) {
    dispatch(setResolverTestResult(null));
    return error;
  }
};

/* ------------------------------------- Pending approvals ------------------------------------- */

/**
 * The approval queue. The endpoint takes `limit` and `origin` only - there is no per-domain
 * filter, and the queue is environment-wide by design, because a user who resolved to nothing
 * has no domain to file them under on the screen.
 *
 * The query is built from the parameters the endpoint actually reads, and omitted entirely
 * when there are none: the previous `{ ...params }` on an undefined argument produced an empty
 * object, which constructEndpoint rendered as a bare trailing '?'.
 */
export const getUsersPendingApprovalAsync = (params = {}) => async (dispatch) => {
  const { limit = 100, origin } = params;
  const query = { limit, ...(origin ? { origin } : {}) };
  try {
    const data = await request(constructEndpoint(GET_USERS_PENDING_APPROVAL, query), {
      method: 'GET',
      loadingKey: LOADING_KEYS.SSO_PENDING_APPROVALS,
      skipToast: true,
    });
    if (data?.status === SUCCESS_STATUS) dispatch(setPending({ items: data.data?.items }));
    else dispatch(setPendingFailed());
  } catch {
    dispatch(setPendingFailed());
  }
};

export const approvePendingUserAsync = (queueId, payload) => async (dispatch) => {
  try {
    const data = await request(APPROVE_PENDING_USER, {
      method: 'POST',
      body: JSON.stringify({ queueId, ...payload }),
      loadingKey: LOADING_KEYS.SSO_PENDING_APPROVALS,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(getUsersPendingApprovalAsync());
    }
    return data;
  } catch (error) {
    return error;
  }
};
